import { formatBrl } from '@estac1/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  Spinner,
  toast,
  useConfirm,
} from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api.js';
import { br, FreePeriodForm } from './free-period-form.js';

type ChargeStatus = 'pending' | 'overdue' | 'paid' | 'refunded' | 'other';

interface Charge {
  id: string;
  status: ChargeStatus;
  rawStatus: string;
  amountCents: number;
  dueDate: string | null;
  invoiceUrl: string | null;
  providerSubscriptionId: string | null;
}

interface LotBilling {
  available: boolean;
  subscription: { status: string; amountCents: number; nextDueDate: string | null } | null;
  charges: Charge[];
}

const STATUS: Record<ChargeStatus, { label: string; variant: 'default' | 'danger' | 'success' }> = {
  pending: { label: 'pendente', variant: 'default' },
  overdue: { label: 'vencida', variant: 'danger' },
  paid: { label: 'paga', variant: 'success' },
  refunded: { label: 'estornada', variant: 'default' },
  other: { label: '', variant: 'default' },
};

const SUBSCRIPTION_STATUS: Record<string, string> = {
  active: 'ativa',
  inactive: 'inativa',
  expired: 'expirada',
};

const isOpen = (c: Charge) => c.status === 'pending' || c.status === 'overdue';

/**
 * A cobrança do cliente no Asaas (#710): a assinatura, as cobranças com a opção
 * de remover e o período grátis.
 */
export const BillingSection = ({ lotId }: { lotId: string }) => {
  const qc = useQueryClient();
  const billing = useQuery({
    queryKey: ['admin-client-billing', lotId],
    queryFn: () => api.request<LotBilling>(`/v1/admin/clients/${lotId}/billing`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin-client-billing', lotId] });
    qc.invalidateQueries({ queryKey: ['admin-client-overview', lotId] });
    qc.invalidateQueries({ queryKey: ['admin-clients'] });
  };

  const [pendingCharge, setPendingCharge] = useState<Charge | null>(null);
  const confirm = useConfirm();
  const remove = useMutation({
    mutationFn: (paymentId: string) =>
      api.request(`/v1/admin/clients/${lotId}/charges/${paymentId}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Cobrança removida no Asaas, sem avisar o cliente.');
      refresh();
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível remover a cobrança.'),
  });

  const data = billing.data;
  const sub = data?.subscription;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6 text-sm">
        <p className="font-medium">Cobrança no Asaas</p>
        {billing.isLoading && <Spinner />}
        {billing.isError && <p className="text-danger">Não foi possível ler o Asaas.</p>}
        {data && !data.available && (
          <p className="text-muted-fg">O Asaas não está configurado neste ambiente.</p>
        )}
        {data?.available && (
          <>
            <p>
              {sub
                ? `Assinatura de ${formatBrl(sub.amountCents)} por mês, ${SUBSCRIPTION_STATUS[sub.status] ?? sub.status}, com a próxima cobrança em ${br(sub.nextDueDate)}.`
                : 'Sem assinatura no Asaas.'}
            </p>
            <ul className="space-y-1">
              {data.charges.length === 0 && <li className="text-muted-fg">Nenhuma cobrança.</li>}
              {data.charges.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-2">
                  <span>
                    {br(c.dueDate)} · {formatBrl(c.amountCents)}
                    {c.providerSubscriptionId ? '' : ' · avulsa'}
                  </span>
                  <span className="flex items-center gap-2">
                    <Badge variant={STATUS[c.status].variant}>
                      {STATUS[c.status].label || c.rawStatus}
                    </Badge>
                    {c.invoiceUrl && (
                      <a
                        href={c.invoiceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline"
                      >
                        ver
                      </a>
                    )}
                    {isOpen(c) && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={remove.isPending}
                        onClick={() => {
                          setPendingCharge(c);
                          confirm.confirm(() => remove.mutateAsync(c.id));
                        }}
                      >
                        Remover
                      </Button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
        <FreePeriodForm lotId={lotId} onDone={refresh} />
      </CardContent>
      <ConfirmDialog
        {...confirm.dialogProps}
        title="Remover cobrança?"
        description={
          pendingCharge
            ? `Remove no Asaas a cobrança de ${formatBrl(pendingCharge.amountCents)} com vencimento em ${br(pendingCharge.dueDate)}. O cliente não é avisado.`
            : ''
        }
        confirmLabel="Remover"
        destructive
        pending={remove.isPending}
      />
    </Card>
  );
};
